
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';

interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    // Navigation
    home: 'Home',
    promo: 'Deals',
    grocery: 'Marketplace',
    profile: 'Profile',
    
    // Profile Page
    join_gambgo: 'Join GAMBGO',
    become_vendor: 'Become a Vendor',
    become_driver: 'Become a Driver',
    business_portal: 'Business Portal',
    manage_business: 'Manage Business',
    driver_portal: 'Driver Portal',
    drive: 'DRIVE',
    general: 'General',
    order_history: 'Order History',
    favorites: 'Favorites',
    edit_profile: 'Edit Profile',
    app_settings: 'App Settings',
    language: 'Language',
    help_support: 'Help & Support',
    sign_out: 'Sign Out',
    
    // Location
    delivery_location: 'Delivery Location',
    enter_address: 'Enter Your Address',
    street_address: 'Street address',
    city: 'City',
    save_address: 'Save Address',
    use_current_location: 'Use Current Location',
    home_address: 'Home',
    work_address: 'Work',
    previous_locations: 'Previous Locations',
    your_locations: 'Your Locations',
    
    // Homepage categories and sections
    fast_food: 'Fast Food',
    coffee: 'Coffee',
    healthy: 'Healthy',
    dessert: 'Dessert',
    fine_dining: 'Fine Dining',
    casual_dining: 'Casual Dining',
    bakery: 'Bakery',
    featured_restaurants: 'Featured Restaurants',
    new_to_you: 'New to You',
    top_picks: 'Top Picks',
    all_restaurants: 'All Restaurants',
    clear_filter: 'Clear Filter',
    no_restaurants_available: 'No restaurants available yet.',
    check_back_later: 'Check back later!',
    no_new_restaurants: 'No new restaurants to show.',
    all_restaurants_old: 'All restaurants have been around for a while.',
    no_top_picks: 'No top picks available.',
    finding_restaurants: 'We\'re working on finding great restaurants for you!',
    adding_restaurants: 'We\'re working on adding restaurants to your area!',
    
    // Restaurant status
    open: 'Open',
    closed: 'Closed',
    closing_soon: 'Closing Soon',
    
    // Vendor Onboarding
    become_a_vendor: 'Become a Vendor',
    vendor_application: 'Vendor Application',
    vendor_application_desc: 'Join GAMBGO as a restaurant partner and start reaching more customers',
    business_name: 'Business Name',
    business_address: 'Business Address',
    phone_number: 'Phone Number',
    business_description: 'Business Description',
    business_categories: 'Business Categories',
    enter_business_name: 'Enter your business name',
    search_business_location: 'Search for your business location...',
    tell_us_about_business: 'Tell us about your business...',
    submit_application: 'Submit Application',
    submitting: 'Submitting...',
    
    // Promotions
    special_offers: 'Special Offers',
    save_more_meals: 'Save more on your favorite meals',
    no_active_promotions: 'No active promotions',
    check_back_deals: 'Check back later for exciting deals and offers!',
    copied: 'Copied!',
    promo_code_copied: 'Promo code copied to clipboard',
    copy_code: 'Copy Code',
    minimum_order: 'Minimum order',
    maximum_discount: 'Maximum discount',
    valid_until: 'Valid until',
    remaining_uses: 'Remaining uses',
    expires_soon: 'Expires Soon',
    
    // Favorites
    no_favorites_yet: 'No favorites yet',
    browse_restaurants_save: 'Browse restaurants and tap the heart icon to save your favorites',
    explore_restaurants: 'Explore Restaurants',
    
    // Marketplace
    grocery_delivery: 'Marketplace',
    fresh_groceries_delivered: 'Shop for everything you need',
    coming_soon_gambgo: 'Coming soon to GAMBGO!',
    
    // Business Hours
    business_hours: 'Business Hours',
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
    copy_to_all: 'Copy to all',
    open_time: 'Open Time',
    close_time: 'Close Time',
    save_business_hours: 'Save Business Hours',
    saving: 'Saving...',
    
    // Common
    name: 'Name',
    email: 'Email',
    role: 'Role',
    customer: 'Customer',
    business_owner: 'Business Owner',
    driver: 'Driver',
    admin: 'Admin',
    loading: 'Loading...',
    success: 'Success',
    error: 'Error',
    required: 'required',
    min: 'min',
    off: 'OFF',
  },
  fr: {
    // Navigation
    home: 'Accueil',
    promo: 'Offres',
    grocery: 'Marché',
    profile: 'Profil',
    
    // Profile Page
    join_gambgo: 'Rejoindre GAMBGO',
    become_vendor: 'Devenir Vendeur',
    become_driver: 'Devenir Chauffeur',
    business_portal: 'Portail Business',
    manage_business: 'Gérer Business',
    driver_portal: 'Portail Chauffeur',
    drive: 'CONDUIRE',
    general: 'Général',
    order_history: 'Historique des Commandes',
    favorites: 'Favoris',
    edit_profile: 'Modifier le Profil',
    app_settings: 'Paramètres',
    language: 'Langue',
    help_support: 'Aide & Support',
    sign_out: 'Se Déconnecter',
    
    // Location
    delivery_location: 'Lieu de Livraison',
    enter_address: 'Entrez Votre Adresse',
    street_address: 'Adresse de la rue',
    city: 'Ville',
    save_address: 'Sauvegarder Adresse',
    use_current_location: 'Utiliser Position Actuelle',
    home_address: 'Domicile',
    work_address: 'Travail',
    previous_locations: 'Emplacements Précédents',
    your_locations: 'Vos Emplacements',
    
    // Homepage categories and sections
    fast_food: 'Restauration Rapide',
    coffee: 'Café',
    healthy: 'Sain',
    dessert: 'Dessert',
    fine_dining: 'Gastronomie',
    casual_dining: 'Décontracté',
    bakery: 'Boulangerie',
    featured_restaurants: 'Restaurants Vedettes',
    new_to_you: 'Nouveau pour Vous',
    top_picks: 'Meilleurs Choix',
    all_restaurants: 'Tous les Restaurants',
    clear_filter: 'Effacer le Filtre',
    no_restaurants_available: 'Aucun restaurant disponible pour le moment.',
    check_back_later: 'Revenez plus tard!',
    no_new_restaurants: 'Aucun nouveau restaurant à afficher.',
    all_restaurants_old: 'Tous les restaurants existent depuis un moment.',
    no_top_picks: 'Aucun meilleur choix disponible.',
    finding_restaurants: 'Nous travaillons à trouver de bons restaurants pour vous!',
    adding_restaurants: 'Nous travaillons à ajouter des restaurants dans votre région!',
    
    // Restaurant status
    open: 'Ouvert',
    closed: 'Fermé',
    closing_soon: 'Ferme Bientôt',
    
    // Vendor Onboarding
    become_a_vendor: 'Devenir Vendeur',
    vendor_application: 'Candidature Vendeur',
    vendor_application_desc: 'Rejoignez GAMBGO comme partenaire restaurant et commencez à atteindre plus de clients',
    business_name: 'Nom de l\'Entreprise',
    business_address: 'Adresse de l\'Entreprise',
    phone_number: 'Numéro de Téléphone',
    business_description: 'Description de l\'Entreprise',
    business_categories: 'Catégories d\'Entreprise',
    enter_business_name: 'Entrez le nom de votre entreprise',
    search_business_location: 'Recherchez l\'emplacement de votre entreprise...',
    tell_us_about_business: 'Parlez-nous de votre entreprise...',
    submit_application: 'Soumettre la Candidature',
    submitting: 'Soumission...',
    
    // Promotions
    special_offers: 'Offres Spéciales',
    save_more_meals: 'Économisez plus sur vos repas préférés',
    no_active_promotions: 'Aucune promotion active',
    check_back_deals: 'Revenez plus tard pour des offres et promotions passionnantes!',
    copied: 'Copié!',
    promo_code_copied: 'Code promo copié dans le presse-papiers',
    copy_code: 'Copier le Code',
    minimum_order: 'Commande minimum',
    maximum_discount: 'Remise maximum',
    valid_until: 'Valide jusqu\'au',
    remaining_uses: 'Utilisations restantes',
    expires_soon: 'Expire Bientôt',
    
    // Favorites
    no_favorites_yet: 'Aucun favori pour le moment',
    browse_restaurants_save: 'Parcourez les restaurants et appuyez sur l\'icône cœur pour sauvegarder vos favoris',
    explore_restaurants: 'Explorer les Restaurants',
    
    // Marketplace
    grocery_delivery: 'Marché',
    fresh_groceries_delivered: 'Achetez tout ce dont vous avez besoin',
    coming_soon_gambgo: 'Bientôt disponible sur GAMBGO!',
    
    // Business Hours
    business_hours: 'Heures d\'Ouverture',
    monday: 'Lundi',
    tuesday: 'Mardi',
    wednesday: 'Mercredi',
    thursday: 'Jeudi',
    friday: 'Vendredi',
    saturday: 'Samedi',
    sunday: 'Dimanche',
    copy_to_all: 'Copier à tous',
    open_time: 'Heure d\'Ouverture',
    close_time: 'Heure de Fermeture',
    save_business_hours: 'Sauvegarder les Heures',
    saving: 'Sauvegarde...',
    
    // Common
    name: 'Nom',
    email: 'Email',
    role: 'Rôle',
    customer: 'Client',
    business_owner: 'Propriétaire Business',
    driver: 'Chauffeur',
    admin: 'Admin',
    loading: 'Chargement...',
    success: 'Succès',
    error: 'Erreur',
    required: 'requis',
    min: 'min',
    off: 'RÉDUC',
  },
  de: {
    // Navigation
    home: 'Startseite',
    promo: 'Angebote',
    grocery: 'Marktplatz',
    profile: 'Profil',
    
    // Profile Page
    join_gambgo: 'GAMBGO Beitreten',
    become_vendor: 'Verkäufer Werden',
    become_driver: 'Fahrer Werden',
    business_portal: 'Business Portal',
    manage_business: 'Business Verwalten',
    driver_portal: 'Fahrer Portal',
    drive: 'FAHREN',
    general: 'Allgemein',
    order_history: 'Bestellhistorie',
    favorites: 'Favoriten',
    edit_profile: 'Profil Bearbeiten',
    app_settings: 'App Einstellungen',
    language: 'Sprache',
    help_support: 'Hilfe & Support',
    sign_out: 'Abmelden',
    
    // Location
    delivery_location: 'Lieferort',
    enter_address: 'Adresse Eingeben',
    street_address: 'Straßenadresse',
    city: 'Stadt',
    save_address: 'Adresse Speichern',
    use_current_location: 'Aktuelle Position Verwenden',
    home_address: 'Zuhause',
    work_address: 'Arbeit',
    previous_locations: 'Vorherige Standorte',
    your_locations: 'Ihre Standorte',
    
    // Homepage categories and sections
    fast_food: 'Fast Food',
    coffee: 'Kaffee',
    healthy: 'Gesund',
    dessert: 'Dessert',
    fine_dining: 'Gehobene Küche',
    casual_dining: 'Ungezwungen',
    bakery: 'Bäckerei',
    featured_restaurants: 'Empfohlene Restaurants',
    new_to_you: 'Neu für Sie',
    top_picks: 'Top Auswahl',
    all_restaurants: 'Alle Restaurants',
    clear_filter: 'Filter Löschen',
    no_restaurants_available: 'Noch keine Restaurants verfügbar.',
    check_back_later: 'Schauen Sie später wieder vorbei!',
    no_new_restaurants: 'Keine neuen Restaurants zu zeigen.',
    all_restaurants_old: 'Alle Restaurants gibt es schon eine Weile.',
    no_top_picks: 'Keine Top Auswahl verfügbar.',
    finding_restaurants: 'Wir arbeiten daran, tolle Restaurants für Sie zu finden!',
    adding_restaurants: 'Wir arbeiten daran, Restaurants in Ihrer Gegend hinzuzufügen!',
    
    // Restaurant status
    open: 'Geöffnet',
    closed: 'Geschlossen',
    closing_soon: 'Schließt Bald',
    
    // Vendor Onboarding
    become_a_vendor: 'Verkäufer Werden',
    vendor_application: 'Verkäufer Bewerbung',
    vendor_application_desc: 'Treten Sie GAMBGO als Restaurant-Partner bei und erreichen Sie mehr Kunden',
    business_name: 'Firmenname',
    business_address: 'Firmenadresse',
    phone_number: 'Telefonnummer',
    business_description: 'Firmenbeschreibung',
    business_categories: 'Firmenkategorien',
    enter_business_name: 'Geben Sie Ihren Firmennamen ein',
    search_business_location: 'Suchen Sie nach Ihrem Firmenstandort...',
    tell_us_about_business: 'Erzählen Sie uns von Ihrem Unternehmen...',
    submit_application: 'Bewerbung Einreichen',
    submitting: 'Einreichen...',
    
    // Promotions
    special_offers: 'Spezielle Angebote',
    save_more_meals: 'Sparen Sie mehr bei Ihren Lieblingsgerichten',
    no_active_promotions: 'Keine aktiven Aktionen',
    check_back_deals: 'Schauen Sie später für aufregende Angebote und Aktionen vorbei!',
    copied: 'Kopiert!',
    promo_code_copied: 'Aktionscode in die Zwischenablage kopiert',
    copy_code: 'Code Kopieren',
    minimum_order: 'Mindestbestellung',
    maximum_discount: 'Maximaler Rabatt',
    valid_until: 'Gültig bis',
    remaining_uses: 'Verbleibende Nutzungen',
    expires_soon: 'Läuft Bald Ab',
    
    // Favorites
    no_favorites_yet: 'Noch keine Favoriten',
    browse_restaurants_save: 'Durchsuchen Sie Restaurants und tippen Sie auf das Herz-Symbol, um Ihre Favoriten zu speichern',
    explore_restaurants: 'Restaurants Erkunden',
    
    // Marketplace
    grocery_delivery: 'Marktplatz',
    fresh_groceries_delivered: 'Kaufen Sie alles was Sie brauchen',
    coming_soon_gambgo: 'Kommt bald zu GAMBGO!',
    
    // Business Hours
    business_hours: 'Öffnungszeiten',
    monday: 'Montag',
    tuesday: 'Dienstag',
    wednesday: 'Mittwoch',
    thursday: 'Donnerstag',
    friday: 'Freitag',
    saturday: 'Samstag',
    sunday: 'Sonntag',
    copy_to_all: 'Auf alle kopieren',
    open_time: 'Öffnungszeit',
    close_time: 'Schließzeit',
    save_business_hours: 'Öffnungszeiten Speichern',
    saving: 'Speichern...',
    
    // Common
    name: 'Name',
    email: 'Email',
    role: 'Rolle',
    customer: 'Kunde',
    business_owner: 'Geschäftsinhaber',
    driver: 'Fahrer',
    admin: 'Admin',
    loading: 'Laden...',
    success: 'Erfolg',
    error: 'Fehler',
    required: 'erforderlich',
    min: 'Min',
    off: 'RABATT',
  },
  es: {
    // Navigation
    home: 'Inicio',
    promo: 'Ofertas',
    grocery: 'Mercado',
    profile: 'Perfil',
    
    // Profile Page
    join_gambgo: 'Unirse a GAMBGO',
    become_vendor: 'Ser Vendedor',
    become_driver: 'Ser Conductor',
    business_portal: 'Portal de Negocio',
    manage_business: 'Gestionar Negocio',
    driver_portal: 'Portal Conductor',
    drive: 'CONDUCIR',
    general: 'General',
    order_history: 'Historial de Pedidos',
    favorites: 'Favoritos',
    edit_profile: 'Editar Perfil',
    app_settings: 'Configuración',
    language: 'Idioma',
    help_support: 'Ayuda y Soporte',
    sign_out: 'Cerrar Sesión',
    
    // Location
    delivery_location: 'Lugar de Entrega',
    enter_address: 'Ingrese Su Dirección',
    street_address: 'Dirección de la calle',
    city: 'Ciudad',
    save_address: 'Guardar Dirección',
    use_current_location: 'Usar Ubicación Actual',
    home_address: 'Casa',
    work_address: 'Trabajo',
    previous_locations: 'Ubicaciones Anteriores',
    your_locations: 'Sus Ubicaciones',
    
    // Homepage categories and sections
    fast_food: 'Comida Rápida',
    coffee: 'Café',
    healthy: 'Saludable',
    dessert: 'Postre',
    fine_dining: 'Alta Cocina',
    casual_dining: 'Casual',
    bakery: 'Panadería',
    featured_restaurants: 'Restaurantes Destacados',
    new_to_you: 'Nuevo para Ti',
    top_picks: 'Mejores Opciones',
    all_restaurants: 'Todos los Restaurantes',
    clear_filter: 'Limpiar Filtro',
    no_restaurants_available: 'No hay restaurantes disponibles aún.',
    check_back_later: '¡Vuelve más tarde!',
    no_new_restaurants: 'No hay restaurantes nuevos para mostrar.',
    all_restaurants_old: 'Todos los restaurantes han estado aquí por un tiempo.',
    no_top_picks: 'No hay mejores opciones disponibles.',
    finding_restaurants: '¡Estamos trabajando en encontrar buenos restaurantes para ti!',
    adding_restaurants: '¡Estamos trabajando en agregar restaurantes a tu área!',
    
    // Restaurant status
    open: 'Abierto',
    closed: 'Cerrado',
    closing_soon: 'Cerrando Pronto',
    
    // Vendor Onboarding
    become_a_vendor: 'Ser Vendedor',
    vendor_application: 'Solicitud de Vendedor',
    vendor_application_desc: 'Únete a GAMBGO como socio restaurante y comienza a llegar a más clientes',
    business_name: 'Nombre del Negocio',
    business_address: 'Dirección del Negocio',
    phone_number: 'Número de Teléfono',
    business_description: 'Descripción del Negocio',
    business_categories: 'Categorías del Negocio',
    enter_business_name: 'Ingrese el nombre de su negocio',
    search_business_location: 'Busque la ubicación de su negocio...',
    tell_us_about_business: 'Cuéntanos sobre tu negocio...',
    submit_application: 'Enviar Solicitud',
    submitting: 'Enviando...',
    
    // Promotions
    special_offers: 'Ofertas Especiales',
    save_more_meals: 'Ahorra más en tus comidas favoritas',
    no_active_promotions: 'No hay promociones activas',
    check_back_deals: '¡Vuelve más tarde para ofertas y promociones emocionantes!',
    copied: '¡Copiado!',
    promo_code_copied: 'Código promocional copiado al portapapeles',
    copy_code: 'Copiar Código',
    minimum_order: 'Pedido mínimo',
    maximum_discount: 'Descuento máximo',
    valid_until: 'Válido hasta',
    remaining_uses: 'Usos restantes',
    expires_soon: 'Expira Pronto',
    
    // Favorites
    no_favorites_yet: 'Aún no hay favoritos',
    browse_restaurants_save: 'Navega por los restaurantes y toca el ícono del corazón para guardar tus favoritos',
    explore_restaurants: 'Explorar Restaurantes',
    
    // Marketplace
    grocery_delivery: 'Mercado',
    fresh_groceries_delivered: 'Compra todo lo que necesitas',
    coming_soon_gambgo: '¡Próximamente en GAMBGO!',
    
    // Business Hours
    business_hours: 'Horarios de Negocio',
    monday: 'Lunes',
    tuesday: 'Martes',
    wednesday: 'Miércoles',
    thursday: 'Jueves',
    friday: 'Viernes',
    saturday: 'Sábado',
    sunday: 'Domingo',
    copy_to_all: 'Copiar a todos',
    open_time: 'Hora de Apertura',
    close_time: 'Hora de Cierre',
    save_business_hours: 'Guardar Horarios',
    saving: 'Guardando...',
    
    // Common
    name: 'Nombre',
    email: 'Email',
    role: 'Rol',
    customer: 'Cliente',
    business_owner: 'Propietario de Negocio',
    driver: 'Conductor',
    admin: 'Admin',
    loading: 'Cargando...',
    success: 'Éxito',
    error: 'Error',
    required: 'requerido',
    min: 'min',
    off: 'DESC',
  },
  pt: {
    // Navigation
    home: 'Início',
    promo: 'Ofertas',
    grocery: 'Mercado',
    profile: 'Perfil',
    
    // Profile Page
    join_gambgo: 'Juntar-se ao GAMBGO',
    become_vendor: 'Tornar-se Vendedor',
    become_driver: 'Tornar-se Motorista',
    business_portal: 'Portal de Negócios',
    manage_business: 'Gerir Negócio',
    driver_portal: 'Portal Motorista',
    drive: 'CONDUZIR',
    general: 'Geral',
    order_history: 'Histórico de Pedidos',
    favorites: 'Favoritos',
    edit_profile: 'Editar Perfil',
    app_settings: 'Configurações',
    language: 'Idioma',
    help_support: 'Ajuda e Suporte',
    sign_out: 'Sair',
    
    // Location
    delivery_location: 'Local de Entrega',
    enter_address: 'Digite Seu Endereço',
    street_address: 'Endereço da rua',
    city: 'Cidade',
    save_address: 'Salvar Endereço',
    use_current_location: 'Usar Localização Atual',
    home_address: 'Casa',
    work_address: 'Trabalho',
    previous_locations: 'Localizações Anteriores',
    your_locations: 'Suas Localizações',
    
    // Homepage categories and sections
    fast_food: 'Comida Rápida',
    coffee: 'Café',
    healthy: 'Saudável',
    dessert: 'Sobremesa',
    fine_dining: 'Culinária Refinada',
    casual_dining: 'Casual',
    bakery: 'Padaria',
    featured_restaurants: 'Restaurantes em Destaque',
    new_to_you: 'Novo para Você',
    top_picks: 'Melhores Escolhas',
    all_restaurants: 'Todos os Restaurantes',
    clear_filter: 'Limpar Filtro',
    no_restaurants_available: 'Nenhum restaurante disponível ainda.',
    check_back_later: 'Volte mais tarde!',
    no_new_restaurants: 'Nenhum restaurante novo para mostrar.',
    all_restaurants_old: 'Todos os restaurantes existem há um tempo.',
    no_top_picks: 'Nenhuma melhor escolha disponível.',
    finding_restaurants: 'Estamos trabalhando para encontrar ótimos restaurantes para você!',
    adding_restaurants: 'Estamos trabalhando para adicionar restaurantes à sua área!',
    
    // Restaurant status
    open: 'Aberto',
    closed: 'Fechado',
    closing_soon: 'Fechando em Breve',
    
    // Vendor Onboarding
    become_a_vendor: 'Tornar-se Vendedor',
    vendor_application: 'Candidatura de Vendedor',
    vendor_application_desc: 'Junte-se ao GAMBGO como parceiro restaurante e comece a alcançar mais clientes',
    business_name: 'Nome do Negócio',
    business_address: 'Endereço do Negócio',
    phone_number: 'Número de Telefone',
    business_description: 'Descrição do Negócio',
    business_categories: 'Categorias do Negócio',
    enter_business_name: 'Digite o nome do seu negócio',
    search_business_location: 'Procure a localização do seu negócio...',
    tell_us_about_business: 'Conte-nos sobre o seu negócio...',
    submit_application: 'Enviar Candidatura',
    submitting: 'Enviando...',
    
    // Promotions
    special_offers: 'Ofertas Especiais',
    save_more_meals: 'Economize mais nas suas refeições favoritas',
    no_active_promotions: 'Nenhuma promoção ativa',
    check_back_deals: 'Volte mais tarde para ofertas e promoções emocionantes!',
    copied: 'Copiado!',
    promo_code_copied: 'Código promocional copiado para a área de transferência',
    copy_code: 'Copiar Código',
    minimum_order: 'Pedido mínimo',
    maximum_discount: 'Desconto máximo',
    valid_until: 'Válido até',
    remaining_uses: 'Usos restantes',
    expires_soon: 'Expira em Breve',
    
    // Favorites
    no_favorites_yet: 'Ainda não há favoritos',
    browse_restaurants_save: 'Navegue pelos restaurantes e toque no ícone de coração para salvar seus favoritos',
    explore_restaurants: 'Explorar Restaurantes',
    
    // Marketplace
    grocery_delivery: 'Mercado',
    fresh_groceries_delivered: 'Compre tudo que você precisa',
    coming_soon_gambgo: 'Em breve no GAMBGO!',
    
    // Business Hours
    business_hours: 'Horários de Funcionamento',
    monday: 'Segunda-feira',
    tuesday: 'Terça-feira',
    wednesday: 'Quarta-feira',
    thursday: 'Quinta-feira',
    friday: 'Sexta-feira',
    saturday: 'Sábado',
    sunday: 'Domingo',
    copy_to_all: 'Copiar para todos',
    open_time: 'Hora de Abertura',
    close_time: 'Hora de Fechamento',
    save_business_hours: 'Salvar Horários',
    saving: 'Salvando...',
    
    // Common
    name: 'Nome',
    email: 'Email',
    role: 'Papel',
    customer: 'Cliente',
    business_owner: 'Proprietário de Negócio',
    driver: 'Motorista',
    admin: 'Admin',
    loading: 'Carregando...',
    success: 'Sucesso',
    error: 'Erro',
    required: 'obrigatório',
    min: 'min',
    off: 'DESC',
  }
};

const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key: string) => key,
});

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguageState] = useState('en');
  const { user } = useAuth();

  useEffect(() => {
    // Load language from user profile if available, otherwise from localStorage
    if (user) {
      fetchUserLanguage();
    } else {
      const savedLang = localStorage.getItem('gambgo_language') || 'en';
      console.log('Loading language from localStorage:', savedLang);
      setLanguageState(savedLang);
    }
  }, [user]);

  const fetchUserLanguage = async () => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('language_preference')
        .eq('id', user?.id)
        .single();
      
      if (data?.language_preference) {
        console.log('Loading language from profile:', data.language_preference);
        setLanguageState(data.language_preference);
        // Also update localStorage to keep in sync
        localStorage.setItem('gambgo_language', data.language_preference);
      } else {
        // Fallback to localStorage if no profile preference
        const savedLang = localStorage.getItem('gambgo_language') || 'en';
        setLanguageState(savedLang);
      }
    } catch (error) {
      console.log('Error fetching language preference:', error);
      // Fallback to localStorage
      const savedLang = localStorage.getItem('gambgo_language') || 'en';
      setLanguageState(savedLang);
    }
  };

  const setLanguage = async (lang: string) => {
    console.log('Setting language to:', lang);
    setLanguageState(lang);
    localStorage.setItem('gambgo_language', lang);
    
    // Update user profile if logged in
    if (user) {
      try {
        await supabase
          .from('profiles')
          .update({ language_preference: lang })
          .eq('id', user.id);
        console.log('Language preference updated in database');
      } catch (error) {
        console.log('Error updating language preference:', error);
      }
    }
  };

  const t = (key: string): string => {
    const translation = translations[language as keyof typeof translations]?.[key as keyof typeof translations.en];
    return translation || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
